import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { 
  User, 
  CheckCircle2, 
  XCircle,
  Clock,
  Mail,
  MessageSquare,
  Building2,
  Users,
  UserCircle,
  Star
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminApplications() {
  const { t } = useTranslation();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const appsRes = await axios.get(`${API}/project-applications`, {
        params: statusFilter ? { status: statusFilter } : {}
      });
      
      setApplications(appsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (applicationId, newStatus) => {
    try {
      await axios.put(`${API}/project-applications/${applicationId}/status?status=${newStatus}`);
      toast.success(`Application ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    accepted: "bg-green-500/20 text-green-400 border-green-500/30",
    rejected: "bg-red-500/20 text-red-400 border-red-500/30",
  };

  return (
    <DashboardLayout navItems={adminNavItems} title="Applications">
      <div className="space-y-6" data-testid="admin-applications">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-['Unbounded'] font-semibold text-white">
              Scaffolder Applications
            </h2>
            <p className="text-[#94A3B8]">Review applications from workers with team details</p>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-black/50 border border-white/10 text-white px-4 py-2 rounded-lg focus:border-[#23F7DD]"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-500/10 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-yellow-400">
                    {applications.filter(a => a.status === "pending").length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-green-500">
                    {applications.filter(a => a.status === "accepted").length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">Accepted</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-500/10 rounded-lg">
                  <XCircle className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-red-500">
                    {applications.filter(a => a.status === "rejected").length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">Rejected</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Applications List */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-12">
                <User className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                <p className="text-[#94A3B8]">No applications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {applications.map((app) => (
                  <div
                    key={app.application_id}
                    className="p-6"
                    data-testid={`application-${app.application_id}`}
                  >
                    <div className="flex flex-col gap-4">
                      {/* Header Row */}
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        {/* Applicant Info */}
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-[#23F7DD]/20 flex items-center justify-center flex-shrink-0">
                            <User className="w-6 h-6 text-[#23F7DD]" />
                          </div>
                          <div>
                            <p className="text-white font-medium text-lg">{app.user_name}</p>
                            <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
                              <Mail className="w-4 h-4" />
                              {app.user_email}
                            </div>
                            <div className="flex items-center gap-2 mt-2 text-sm text-[#94A3B8]">
                              <Building2 className="w-4 h-4 text-[#23F7DD]" />
                              <span className="text-white">{app.project_title || "Unknown Project"}</span>
                              <span>• {app.project_city}</span>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[app.status]}`}>
                            {app.status}
                          </span>

                          {app.status === "pending" && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleUpdateStatus(app.application_id, "accepted")}
                                className="bg-green-500 text-white hover:bg-green-600"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateStatus(app.application_id, "rejected")}
                                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Team Leader */}
                      {app.team_leader_name && (
                        <div className="ml-16 p-3 bg-[#23F7DD]/5 rounded-lg border border-[#23F7DD]/20">
                          <div className="flex items-center gap-2">
                            <UserCircle className="w-5 h-5 text-[#23F7DD]" />
                            <span className="text-[#94A3B8] text-sm">Team Leader:</span>
                            <span className="text-white font-medium">{app.team_leader_name}</span>
                          </div>
                        </div>
                      )}

                      {/* Team Info */}
                      {app.team_name && (
                        <div className="ml-16 p-3 bg-purple-500/5 rounded-lg border border-purple-500/20">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-5 h-5 text-purple-400" />
                            <span className="text-[#94A3B8] text-sm">Team:</span>
                            <span className="text-white font-medium">{app.team_name}</span>
                          </div>
                        </div>
                      )}

                      {/* Team Members */}
                      {app.team_members && app.team_members.length > 0 && (
                        <div className="ml-16 p-4 bg-white/5 rounded-lg">
                          <div className="flex items-center gap-2 mb-3">
                            <Users className="w-4 h-4 text-[#23F7DD]" />
                            <span className="text-white text-sm font-medium">
                              Team Members ({app.team_members.length})
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                            {app.team_members.map((member, idx) => (
                              <div 
                                key={idx}
                                className="flex items-center gap-2 p-2 bg-black/30 rounded"
                              >
                                <div className="w-8 h-8 rounded-full bg-[#23F7DD]/10 flex items-center justify-center">
                                  <User className="w-4 h-4 text-[#23F7DD]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-white text-sm truncate">{member.name}</p>
                                  <p className="text-[#475569] text-xs">{member.worker_code}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Message */}
                      {app.message && (
                        <div className="ml-16 p-3 bg-white/5 rounded-lg">
                          <div className="flex items-start gap-2">
                            <MessageSquare className="w-4 h-4 text-[#94A3B8] mt-0.5" />
                            <p className="text-[#94A3B8] text-sm">{app.message}</p>
                          </div>
                        </div>
                      )}

                      <p className="ml-16 text-xs text-[#475569]">
                        Applied {new Date(app.created_at).toLocaleDateString()} at{" "}
                        {new Date(app.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
